import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import './BiohackInfoCard.css';
import { Icon } from './Icon.js';
import BiohackRating from './BiohackRating';
import { shortenUrl } from '../utils/biomarkerUtils';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import AttachmentDisplay from './AttachmentDisplay';
import { fetchBiomarkerAttachments } from './PostFeed';
import { enrichBiomarkerWithRanges } from '../utils/biomarkerUtils';
import { escapeHtml } from '../utils/escapeHtml';

const BiohackInfoCard = ({ biohack, onClose, onAddToProtocol, isFavourited, onToggleFavourite, hasRated = false, onRatingSubmitted, fromLandingPage = false }) => {
  const navigate = useNavigate();
  const [showRelatedPosts, setShowRelatedPosts] = useState(false);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState(null);
  const [showRating, setShowRating] = useState(false);
  const [ratingCount, setRatingCount] = useState(0);
  const [ratingCountLoading, setRatingCountLoading] = useState(true);
  const [userLogsCount, setUserLogsCount] = useState(0);
  const [userLogsLoading, setUserLogsLoading] = useState(true);
  const [favouritesCount, setFavouritesCount] = useState(0);
  const [favouritesLoading, setFavouritesLoading] = useState(true);
  const [fullBiohack, setFullBiohack] = useState(biohack);
  const [disclaimerText, setDisclaimerText] = useState('');

  // Function to fetch rating count
  const fetchRatingCount = async () => {
    try {
      const cacheKey = getCacheKey('BiohackInfoCard_fetchRatingCount', biohack.id);
      const { count, error } = await dedupeRequest(cacheKey, async () => {
        return await supabase
          .from('hack_scoring')
          .select('*', { count: 'exact', head: true })
          .eq('hack_id', biohack.id);
      });

      if (error) throw error;
      setRatingCount(count || 0);
    } catch (err) {
      console.error('Error fetching rating count:', err);
      setRatingCount(0);
    } finally {
      setRatingCountLoading(false);
    }
  };

  // Function to fetch user logs count
  const fetchUserLogsCount = async () => {
    try {
      const cacheKey = getCacheKey('BiohackInfoCard_fetchUserLogsCount', biohack.id);
      const { count, error } = await dedupeRequest(cacheKey, async () => {
        return await supabase
          .from('logging_hack_entries')
          .select('*', { count: 'exact', head: true })
          .eq('hack_id', biohack.id);
      });

      if (error) throw error;
      setUserLogsCount(count || 0);
    } catch (err) {
      console.error('Error fetching user logs count:', err);
      setUserLogsCount(0);
    } finally {
      setUserLogsLoading(false);
    }
  };

  // Function to fetch favourites count
  const fetchFavouritesCount = async () => {
    try {
      const cacheKey = getCacheKey('BiohackInfoCard_fetchFavouritesCount', biohack.id);
      const { count, error } = await dedupeRequest(cacheKey, async () => {
        return await supabase
          .from('hack_favourited')
          .select('*', { count: 'exact', head: true })
          .eq('hack_id', biohack.id);
      });

      if (error) throw error;
      setFavouritesCount(count || 0);
    } catch (err) {
      console.error('Error fetching favourites count:', err);
      setFavouritesCount(0);
    } finally {
      setFavouritesLoading(false);
    }
  };

  // Fetch full biohack data if description or research_text are missing
  useEffect(() => {
    const fetchFullBiohackData = async () => {
      if (!biohack?.id) return;
      
      // Check if we need to fetch full data (missing description or research_text)
      const needsFullData = !biohack.description || !biohack.research_text;
      
      if (needsFullData) {
        try {
          // Deduplicate requests (e.g. React StrictMode double-mount in dev)
          const cacheKey = getCacheKey('fetchFullBiohackData', biohack.id);
          const data = await dedupeRequest(cacheKey, async () => {
            const { data, error } = await supabase
              .from('biohacks')
              .select('*')
              .eq('id', biohack.id)
              .single();
            
            if (error) throw error;
            return data;
          });
          
          // Merge fetched data with existing biohack data (preserve any additional fields)
          setFullBiohack({ ...biohack, ...data });
        } catch (err) {
          console.error('Error fetching full biohack data:', err);
          // If fetch fails, use the original biohack data
          setFullBiohack(biohack);
        }
      } else {
        // Already has full data, use it as is
        setFullBiohack(biohack);
      }
    };

    fetchFullBiohackData();
  }, [biohack]);

  // Fetch disclaimer text from disclaimers table (deduped to avoid double request in Strict Mode)
  useEffect(() => {
    const cacheKey = getCacheKey('BiohackInfoCard_fetchDisclaimer', 'biohack_info_card');
    let cancelled = false;

    const fetchDisclaimer = async () => {
      try {
        const data = await dedupeRequest(cacheKey, async () => {
          const { data: res, error } = await supabase
            .from('disclaimers')
            .select('text')
            .eq('type', 'biohack_info_card')
            .eq('latest', true)
            .maybeSingle();

          if (error) throw error;
          return res;
        });

        if (!cancelled && data?.text) {
          setDisclaimerText(data.text);
        }
      } catch (error) {
        if (!cancelled) console.error('Error fetching disclaimer:', error);
      }
    };

    fetchDisclaimer();
    return () => { cancelled = true; };
  }, []);

  // Fetch rating count when component mounts
  useEffect(() => {
    if (biohack?.id) {
      fetchRatingCount();
      fetchUserLogsCount(); // Fetch user logs count when biohack is available
      fetchFavouritesCount(); // Fetch favourites count when biohack is available
    }
  }, [biohack?.id]);

  // Use fullBiohack for rendering (has all fields)
  const displayBiohack = fullBiohack || biohack;
  
  if (!biohack) return null;

  // Helper function to get score bar color
  const getScoreColor = (score) => {
    if (score <= 1.5) return '#ff4757'; // Red
    if (score <= 3.5) return '#ffa502'; // Yellow/Amber
    return '#2ed573'; // Green
  };

  // Helper function to render score bar (bars only, no numbers)
  const renderScoreBar = (score, label) => {
    const percentage = (score / 5) * 100;
    const color = getScoreColor(score);

    return (
      <div className="score-bar-container">
        <div className="score-label">{label}</div>
        <div className="score-bar">
          <div 
            className="score-bar-fill" 
            style={{ 
              width: `${percentage}%`, 
              backgroundColor: color 
            }}
          />
        </div>
      </div>
    );
  };

  // Helper function to calculate weighted average rating
  const calculateWeightedRating = () => {
    const easeOfUse = displayBiohack.ease_of_use_score || 0;
    const timeEfficiency = displayBiohack.time_efficiency_score || 0;
    const affordability = displayBiohack.affordability_score || 0;
    
    // Check if we have any scores
    if (easeOfUse === 0 && timeEfficiency === 0 && affordability === 0) {
      return null; // No scores available
    }
    
    // Calculate weighted average (equal weights for now, can be adjusted)
    const totalScore = easeOfUse + timeEfficiency + affordability;
    const averageScore = totalScore / 3;
    
    return averageScore.toFixed(1); // Round to 1 decimal place, preserve .0
  };

  // Helper function to format time ago
  const timeAgo = (date) => {
    const now = new Date();
    const d = new Date(date);
    const seconds = Math.floor((now - d) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d`;
    const weeks = Math.floor(days / 7);
    if (weeks < 4) return `${weeks}w`;
    const months = Math.floor(days / 30);
    if (months >= 1 && months < 12) return `${months}mo`;
    if (months < 12) return `${weeks}w`; // 28�29 days: show 4w instead of 0mo
    const years = Math.floor(days / 365);
    return `${years}y`;
  };

  // Helper function to format post content with biohack mentions (copied from PostCard)
  const formatPostContentWithBiohacks = (content, biohackTags = []) => {
    if (!content) return '';
    
    // Always escape content first
    let escapedContent = escapeHtml(content);
    
    // If no biohack tags, convert newlines and return escaped content with line breaks
    if (!biohackTags || biohackTags.length === 0) {
      return escapedContent.replace(/\n/g, '<br>');
    }
    
    // Create a map of biohack names to biohack objects for lookup
    const biohackMap = new Map();
    const seenIds = new Set();
    biohackTags.forEach(tag => {
      if (tag.biohacks?.name && tag.biohacks?.id) {
        if (!seenIds.has(tag.biohacks.id)) {
          biohackMap.set(tag.biohacks.name.toLowerCase(), tag.biohacks);
          seenIds.add(tag.biohacks.id);
        }
      }
    });
    
    const biohackNames = Array.from(biohackMap.keys());
    if (biohackNames.length === 0) return escapedContent.replace(/\n/g, '<br>');
    
    const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const actualNames = Array.from(biohackMap.keys())
      .map(name => biohackMap.get(name).name)
      .sort((a, b) => b.length - a.length);
    const names = actualNames.map(escapeRegExp);
    
    const startBoundary = String.raw`(^|[\\s.,;:!?()[\\]{}"'])`;
    const endBoundary = String.raw`(?=\\s|$|[.,;:!?()[\\]{}"'])`;
    
    let result = escapedContent;
    
    for (let i = 0; i < actualNames.length; i++) {
      const name = actualNames[i];
      const escapedName = names[i];
      const biohack = biohackMap.get(name.toLowerCase());
      
      if (!biohack || !biohack.id) continue;
      
      const pattern = new RegExp(`${startBoundary}(${escapedName})${endBoundary}`, 'gi');
      const escapedId = String(biohack.id).replace(/"/g, '&quot;');
      
      let finalResult = '';
      let lastIndex = 0;
      let match;
      
      pattern.lastIndex = 0;
      
      while ((match = pattern.exec(result)) !== null) {
        const matchStart = match.index;
        const pre = match[1];
        const matchedName = match[2];
        
        finalResult += result.substring(lastIndex, matchStart);
        
        const beforeMatch = finalResult;
        const lastSpanOpen = beforeMatch.lastIndexOf('<span class="biohack-mention"');
        const lastSpanClose = beforeMatch.lastIndexOf('</span>');
        
        if (lastSpanOpen > lastSpanClose) {
          finalResult += match[0];
        } else {
          finalResult += `${pre}<span class="biohack-mention" data-biohack-id="${escapedId}" data-biohack-name="${escapeHtml(biohack.name)}">${matchedName}</span>`;
        }
        
        lastIndex = matchStart + match[0].length;
      }
      
      finalResult += result.substring(lastIndex);
      result = finalResult;
    }
    
    result = result.replace(/\n/g, '<br>');
    return result;
  };

  // Handle rating submission
  const handleRatingSubmitted = (ratings) => {
    // You can add additional logic here if needed
    console.log('Rating submitted:', ratings);
    
    // Refresh the rating count
    fetchRatingCount();
    
    // Notify parent components that rating was updated
    if (onRatingSubmitted) {
      onRatingSubmitted(ratings);
    }
  };

  // Function to fetch related posts (all public posts that tag this biohack, regardless of follow)
  const fetchRelatedPosts = async () => {
    if (showRelatedPosts) {
      setShowRelatedPosts(false);
      return;
    }

    setPostsLoading(true);
    setPostsError(null);

    try {
      let posts = [];

      // Prefer RPC so we see all public posts for this biohack (bypasses RLS/follow restrictions)
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_public_posts_for_biohack', {
        p_biohack_id: biohack.id,
        p_limit: 10
      });

      if (!rpcError && rpcData != null) {
        if (Array.isArray(rpcData)) {
          posts = rpcData;
        } else if (typeof rpcData === 'string') {
          try { posts = JSON.parse(rpcData); } catch { /* ignore */ }
          if (!Array.isArray(posts)) posts = [];
        } else if (typeof rpcData === 'object' && rpcData !== null) {
          // Single jsonb return: sometimes wrapped as [row] or { columnName: value }
          const val = rpcData.get_public_posts_for_biohack ?? Object.values(rpcData)[0];
          const arr = Array.isArray(val) ? val : (Array.isArray(rpcData) ? rpcData : []);
          posts = Array.isArray(arr) ? arr : [];
        }
      } else {
        // Fallback: query only posts that tag this biohack (!inner), then RLS applies
        const { data, error } = await supabase
          .from('posts')
          .select(`
            *,
            user_profiles!posts_user_id_fkey(username, user_level, profile_picture_url, user_id, gender),
            post_attachments:post_attachments_post_id_fkey(
              id, type, sort_order,
              post_attachment_protocols:post_attachment_protocols_post_attachment_id_fkey(
                protocols:protocol_id(
                  id, name, description, pop_score, adherence_score, created_at,
                  protocol_hacks:protocol_hacks_protocol_id_fkey(
                    id, position,
                    biohacks:protocol_hacks_hack_id_fkey(id, name, image_url, category)
                  ),
                  user_profiles!protocols_created_by_fkey(user_id, username)
                )
              ),
              post_attachment_images:post_attachment_images_post_attachment_id_fkey(
                id, image_url, file_name, mime_type
              ),
              post_attachment_reports:post_attachment_reports_post_attachment_id_fkey(
                protocol_report_snapshots:report_id(
                  id, protocol_id, title, snapshot_date, created_at
                )
              )
            ),
            post_biohack_tags:post_biohack_tags_post_id_fkey!inner(biohack_id, biohacks:biohack_id(id, name, image_url, category)),
            post_biomarker_tags:post_biomarker_tags_post_id_fkey(
              biomarkers:biomarker_id(id, name, category, unit)
            )
          `)
          .eq('visibility', 'public')
          .eq('is_deleted', false)
          .eq('post_biohack_tags.biohack_id', biohack.id)
          .order('created_at', { ascending: false })
          .limit(10);

        if (error) throw error;
        posts = data ?? [];
      }

      // Enrich with biomarker results (same as PostFeed) so values show on related posts.
      // RPC returns biomarker definitions only; biomarker_results are fetched client-side
      // and respect RLS (e.g. biomarker_result_attached_to_accessible_post for public posts).
      const postIds = posts.map((p) => p.id);
      if (postIds.length > 0) {
        const biomarkerData = await fetchBiomarkerAttachments(postIds, posts);
        posts = posts.map((post) => {
          if (!biomarkerData[post.id]) return post;
          const attachmentMap = new Map((post.post_attachments || []).map((att) => [att.id, att]));
          Object.values(biomarkerData[post.id]).forEach(({ attachment, biomarkers }) => {
            const existing = attachmentMap.get(attachment.id);
            attachmentMap.set(attachment.id, {
              ...(existing || attachment),
              post_attachment_biomarkers: biomarkers.map((bm) => ({ biomarker_id: bm.id, biomarkers: bm }))
            });
          });
          return { ...post, post_attachments: Array.from(attachmentMap.values()) };
        });
      }

      setRelatedPosts(posts);
      setShowRelatedPosts(true);
    } catch (err) {
      console.error('Error fetching related posts:', err);
      setPostsError('Failed to load related posts');
    } finally {
      setPostsLoading(false);
    }
  };

  const handleFavoriteClick = () => {
    if (fromLandingPage) {
      navigate('/signup');
      return;
    }
    if (onToggleFavourite) {
      onToggleFavourite(biohack.id);
    }
  };

  const handleAddToProtocol = () => {
    if (fromLandingPage) {
      navigate('/signup');
      return;
    }
    if (onAddToProtocol) {
      onAddToProtocol(biohack);
    }
  };

  const handleRatingClick = () => {
    if (fromLandingPage) {
      navigate('/signup');
      return;
    }
    setShowRating(true);
  };

  const handleBuyClick = () => {
    if (fromLandingPage) {
      navigate('/signup');
      return;
    }
    if (displayBiohack.affiliate_link_url) {
      window.open(displayBiohack.affiliate_link_url, '_blank');
    }
  };

  const handleRelatedPostsClick = async () => {
    if (fromLandingPage) {
      navigate('/signup');
      return;
    }
    await fetchRelatedPosts();
  };

  const modalContent = (
    <div className="biohack-info-overlay" onClick={onClose}>
      <div className="biohack-info-card" onClick={(e) => e.stopPropagation()}>
        {/* Header with light blue gradient background */}
        <div className="info-card-header-gradient">
          <div className="mobile-close-handle" onClick={onClose} />
          <div className="header-content">
            <div className="header-left">
              <h2 className="info-card-title">{displayBiohack.name}</h2>
              <div className="biohack-category">{displayBiohack.category || 'Biohack'}</div>
            </div>
            {/* Image positioned in top right corner */}
            {displayBiohack.image_url && (
              <div className="header-right">
                <div className="header-image">
                  <img src={displayBiohack.image_url} alt={displayBiohack.name} />
                  <div className="image-rating">
                    <Icon name="Star" className={`icon--sm ${hasRated ? 'star-rated' : 'star-not-rated'}`} />
                    <span className="rating-text">
                      {calculateWeightedRating() || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Action buttons row */}
          <div className="header-actions">
            <button 
              className="action-btn action-btn--primary"
              onClick={handleRatingClick}
            >
              <Icon 
                name="Star" 
                className={`icon--sm ${hasRated ? 'icon--rated' : 'icon--not-rated'}`}
              />
            </button>
            <button className="action-btn action-btn--secondary" onClick={handleFavoriteClick}>
              <Icon 
                name="Heart" 
                className={`icon--sm ${isFavourited ? 'icon--favourited' : 'icon--not-favourited'}`}
              />
            </button>
            <button className="action-btn action-btn--secondary" onClick={handleAddToProtocol}>
              <Icon name="Plus" className="icon--sm" />
            </button>
            <div className="action-divider"></div>
            <button 
              className="action-btn action-btn--buy" 
              onClick={handleBuyClick}
              title={displayBiohack.affiliate_link_url ? "Amazon affiliate link. As an Amazon Associate, we earn from qualifying purchases, at no extra cost to you." : "Link not available"}
              disabled={!displayBiohack.affiliate_link_url}
            >
              <Icon name="ShoppingBag" className="icon--sm" />
              <span className="buy-text">Buy</span>
            </button>
          </div>
        </div>

        {/* Stats section */}
        <div className="info-card-stats">
          {/* Left side - Star ratings */}
          <div className="stat-item stat-item--ratings">
            <div className="ratings-mini">
              <div className="rating-mini-item">
                <span className="rating-mini-label">Ease of Use</span>
                <div className="rating-mini-stars">
                  {[...Array(5)].map((_, i) => (
                    <Icon 
                      key={i} 
                      name="Star" 
                      className={`icon--xs ${i < Math.round(displayBiohack.ease_of_use_score || 0) ? 'star-filled' : 'star-empty'}`}
                      title={`Ease of Use: ${(displayBiohack.ease_of_use_score || 0).toFixed(1)}/5.0`}
                    />
                  ))}
                </div>
              </div>
              <div className="rating-mini-item">
                <span className="rating-mini-label">Time Efficiency</span>
                <div className="rating-mini-stars">
                  {[...Array(5)].map((_, i) => (
                    <Icon 
                      key={i} 
                      name="Star" 
                      className={`icon--xs ${i < Math.round(displayBiohack.time_efficiency_score || 0) ? 'star-filled' : 'star-empty'}`}
                      title={`Time Efficiency: ${(displayBiohack.time_efficiency_score || 0).toFixed(1)}/5.0`}
                    />
                  ))}
                </div>
              </div>
              <div className="rating-mini-item">
                <span className="rating-mini-label">Affordability</span>
                <div className="rating-mini-stars">
                  {[...Array(5)].map((_, i) => (
                    <Icon 
                      key={i} 
                      name="Star" 
                      className={`icon--xs ${i < Math.round(displayBiohack.affordability_score || 0) ? 'star-filled' : 'star-empty'}`}
                      title={`Affordability: ${(displayBiohack.affordability_score || 0).toFixed(1)}/5.0`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Right side - Stats */}
          <div className="stats-right">
            <div className="stat-item">
              <Icon name="Users" className="icon--sm" />
              <span className="stat-value">
                {userLogsLoading ? '...' : `${userLogsCount}+`}
              </span>
              <span className="stat-label">User Logs</span>
            </div>
            <div className="stat-item">
              <Icon name="Star" className="icon--sm" />
              <span className="stat-value">
                {ratingCountLoading ? '...' : `${ratingCount}+`}
              </span>
              <span className="stat-label">Reviews</span>
            </div>
            <div className="stat-item">
              <Icon name="Heart" className="icon--sm" />
              <span className="stat-value">
                {favouritesLoading ? '...' : `${favouritesCount}+`}
              </span>
              <span className="stat-label">Favourites</span>
            </div>
          </div>
        </div>

        {/* Description */}
        {displayBiohack.description && (
          <div className="info-card-description">
            <h3 className="description-title">What is it?</h3>
            <div className="description-content">
              <p>
                {displayBiohack.description}
                {displayBiohack.description_source_url && (
                  <span className="info-card-source">
                    {' '}Source:{' '}
                    <button
                      type="button"
                      className="source-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(displayBiohack.description_source_url);
                        const originalText = e.target.textContent;
                        e.target.textContent = 'Copied!';
                        setTimeout(() => {
                          e.target.textContent = originalText;
                        }, 2000);
                      }}
                    >
                      {shortenUrl(displayBiohack.description_source_url)}
                    </button>
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Research Text */}
        {displayBiohack.research_text && (
          <div className="info-card-research">
            <h3 className="research-title">Research</h3>
            <div className="research-content">
              <p>
                {displayBiohack.research_text}
                {displayBiohack.research_text_source_url && (
                  <span className="info-card-source">
                    {' '}Source:{' '}
                    <button
                      type="button"
                      className="source-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(displayBiohack.research_text_source_url);
                        const originalText = e.target.textContent;
                        e.target.textContent = 'Copied!';
                        setTimeout(() => {
                          e.target.textContent = originalText;
                        }, 2000);
                      }}
                    >
                      {shortenUrl(displayBiohack.research_text_source_url)}
                    </button>
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        {disclaimerText && (
          <div className="info-card-disclaimer">
            <p className="disclaimer-text">
              {disclaimerText}
            </p>
          </div>
        )}

        {/* Related Content - Below CTA, visually separated */}
        <div className="info-card-related">
          <button 
            className="btn btn--primary related-posts-cta"
            onClick={handleRelatedPostsClick}
            disabled={postsLoading}
          >
            {postsLoading ? (
              'Loading...'
            ) : (
              showRelatedPosts ? 'Hide Related Forum Posts' : 'See Related Forum Posts'
            )}
          </button>
        </div>

        {/* Related Posts Section */}
        {showRelatedPosts && (
          <div className="related-posts-section">
            <div className="related-posts-header">
              <h3 className="related-posts-title">Related Forum Posts</h3>
              <span className="related-posts-count">
                {relatedPosts.length} post{relatedPosts.length !== 1 ? 's' : ''}
              </span>
            </div>
            
            {postsError ? (
              <div className="related-posts-error">
                <p>{postsError}</p>
              </div>
            ) : relatedPosts.length === 0 ? (
              <div className="related-posts-empty">
                <p>No forum posts found for this biohack yet.</p>
                <p>Be the first to share your experience!</p>
              </div>
            ) : (
              <div className="related-posts-list">
                {relatedPosts.map(post => {
                  const username = post.user_profiles?.username || 'User';
                  const level = post.user_profiles?.user_level ?? 1;
                  const postAuthorGender = post.user_profiles?.gender || null;
                  const formattedContent = formatPostContentWithBiohacks(
                    post.content,
                    post.post_biohack_tags || []
                  );
                  const isEdited = post.updated_at && post.updated_at !== post.created_at;
                  const timeAgoLabel = isEdited 
                    ? `${timeAgo(post.updated_at)} (edited)`
                    : timeAgo(post.created_at);

                  // Build attachment modules similar to PostCard
                  const allModules = [];
                  
                  // Add protocols
                  if (post.post_attachments) {
                    const protocolAttachments = post.post_attachments
                      .filter(att => att.type === 'protocols')
                      .flatMap(att => (att.post_attachment_protocols || [])
                        .map(pap => pap.protocols)
                        .filter(p => p && p.id)
                        .map(protocol => ({
                          type: 'protocol',
                          data: protocol,
                          id: protocol.id,
                          sort_order: att.sort_order || 0
                        }))
                      );
                    allModules.push(...protocolAttachments);
                  }
                  
                  // Add images
                  if (post.post_attachments) {
                    const imageAttachments = post.post_attachments
                      .filter(att => att.type === 'image')
                      .flatMap(att => (att.post_attachment_images || []).map(img => ({
                        type: 'image',
                        data: [img],
                        id: 'image-' + img.id,
                        sort_order: att.sort_order || 0
                      })));
                    allModules.push(...imageAttachments);
                  }
                  
                  // Add biomarkers
                  if (post.post_attachments) {
                    const biomarkerAttachments = post.post_attachments.filter(att => att.type === 'biomarker');
                    biomarkerAttachments.forEach(attachment => {
                      const biomarkerData = (attachment.post_attachment_biomarkers || [])
                        .map(biomarkerAtt => biomarkerAtt.biomarkers)
                        .filter(Boolean);
                      
                      if (biomarkerData.length > 0) {
                        biomarkerData.forEach(biomarker => {
                          const enrichedBiomarker = enrichBiomarkerWithRanges(biomarker, postAuthorGender);
                          const labResults = enrichedBiomarker.lab_results || [];
                          const mostRecentResult = labResults.length > 0 ? labResults[0] : null;
                          
                          allModules.push({
                            type: 'biomarker',
                            data: {
                              ...enrichedBiomarker,
                              result: mostRecentResult ? {
                                ...mostRecentResult,
                                panel: mostRecentResult.lab_panels
                              } : null
                            },
                            id: 'biomarker-' + enrichedBiomarker.id,
                            sort_order: attachment.sort_order || 0
                          });
                        });
                      }
                    });
                  }
                  
                  // Add protocol reports
                  if (post.post_attachments) {
                    const protocolReportAttachments = post.post_attachments.filter(att => att.type === 'report');
                    protocolReportAttachments.forEach(attachment => {
                      const protocolReportData = (attachment.post_attachment_reports || [])
                        .map(prAtt => prAtt.protocol_report_snapshots)
                        .filter(Boolean);
                      
                      if (protocolReportData.length > 0) {
                        protocolReportData.forEach(protocolReport => {
                          allModules.push({
                            type: 'protocolReport',
                            data: protocolReport,
                            id: 'protocol-report-' + protocolReport.id,
                            sort_order: attachment.sort_order || 0
                          });
                        });
                      }
                    });
                  }
                  
                  allModules.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

                  return (
                    <div key={post.id} className="related-post-card">
                      <div className="related-post-header">
                        <div className="related-post-avatar">
                          {post.user_profiles?.profile_picture_url ? (
                            <img 
                              src={post.user_profiles.profile_picture_url} 
                              alt={`${username}'s profile`}
                              className="related-post-avatar-image"
                            />
                          ) : (
                            <span className="related-post-avatar-fallback">
                              {username.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="related-post-user-info">
                          <div className="related-post-user-row">
                            <div className="related-post-user">
                              {username}
                              <div className="related-post-level-badge">
                                {level}
                              </div>
                            </div>
                          </div>
                          <div className="related-post-timestamp">
                            {timeAgoLabel}
                            <span className="related-post-visibility-icon-only-inline">
                              <Icon 
                                name={post.visibility === 'public' ? 'Globe' : post.visibility === 'followers' ? 'Users' : 'Lock'} 
                                className="related-post-visibility-icon-inline" 
                              />
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {post.title && (
                        <div className="related-post-title">{post.title}</div>
                      )}
                      <div 
                        className="related-post-content"
                        dangerouslySetInnerHTML={{ __html: formattedContent }}
                      />
                      
                      {allModules.length > 0 && (
                        <div className="related-post-attachments">
                          <AttachmentDisplay
                            modules={allModules}
                            mode="display"
                            onBiohackClick={() => {}}
                            userVotes={{}}
                            handleVote={() => {}}
                            onAddProtocolToStaged={() => {}}
                          />
                        </div>
                      )}
                      
                      <div className="related-post-footer">
                        <div className="related-post-footer-left">
                          <button className="related-post-stat">
                            <Icon name="ThumbsUp" className="icon--sm icon--muted" />
                            <span style={{ fontWeight: 700 }}>{post.likes_count || 0}</span>
                          </button>
                          <button className="related-post-stat">
                            <Icon name="MessageCircle" className="icon--sm icon--muted" />
                            <span style={{ fontWeight: 700 }}>{post.comments_count || 0}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Render the rating modal if needed
  if (showRating) {
    return createPortal(
      <BiohackRating
        biohack={biohack}
        onRatingSubmitted={handleRatingSubmitted}
        onClose={() => setShowRating(false)}
      />,
      document.body
    );
  }

  // Render the modal using a portal to the document body
  return createPortal(modalContent, document.body);
};

export default BiohackInfoCard; 